import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1TutoraiConfigPersonaQueryOptions,
  useGetApiV1TutoraiConfigPersona,
  usePutApiV1TutoraiConfigPersona,
} from '@/shared/api/generated';

import type { TutorPromptScope } from '../types/tutorAi';
import { type TutorPromptFieldState, usePromptDraft } from './usePromptDraft';

/** Params do GET de config no escopo pedido. `undefined` significa escopo global. */
function buildQueryParams(scope: TutorPromptScope) {
  return scope.kind === 'class' ? { classStudentId: scope.classStudentId } : undefined;
}

/**
 * Campo de persona do tutor no escopo global ou de uma turma.
 *
 * A persona define tom e identidade do assistente. Salvar invalida a query do mesmo
 * escopo, para a tela refletir o que o servidor guardou (e não o que ela enviou).
 *
 * @param scope - Escopo global ou de turma
 * @returns Estado do campo e ações de salvar/descartar
 *
 * @example
 * ```tsx
 * const persona = useTutorPersonaConfig({ kind: 'global' });
 * ```
 */
export function useTutorPersonaConfig(scope: TutorPromptScope): TutorPromptFieldState {
  const queryClient = useQueryClient();
  const params = buildQueryParams(scope);

  const query = useGetApiV1TutoraiConfigPersona(params);
  const mutation = usePutApiV1TutoraiConfigPersona({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries(getApiV1TutoraiConfigPersonaQueryOptions(params));
      },
    },
  });

  return usePromptDraft(query, mutation, scope);
}
